import { useEffect } from "react";
import { X, ArrowRight, Monitor, Sun, Moon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme, type ThemePref } from "@/hooks/useTheme";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

const MobileMenu = ({ open, onClose }: MobileMenuProps) => {
  const navigate = useNavigate();
  const { theme, themePref, setThemePref } = useTheme();
  const isLight = theme === "light";

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const handleSignUp = () => {
    onClose();
    navigate("/activate");
  };

  const handleLogin = async () => {
    const { lovable } = await import("@/integrations/lovable");
    await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/dashboard`,
    });
  };

  const goto = (path: string) => {
    onClose();
    navigate(path);
  };

  const themeOptions: { value: ThemePref; label: string; Icon: typeof Sun }[] = [
    { value: "system", label: "System", Icon: Monitor },
    { value: "light", label: "Light", Icon: Sun },
    { value: "dark", label: "Dark", Icon: Moon },
  ];

  // Theme-aware palette that updates instantly
  const bg = isLight ? "#ffffff" : "#0a0a0a";
  const fg = isLight ? "#0a0a0a" : "#ffffff";
  const fgMuted = isLight ? "rgba(10,10,10,0.6)" : "rgba(255,255,255,0.65)";
  const fgFaint = isLight ? "rgba(10,10,10,0.5)" : "rgba(255,255,255,0.5)";
  const border = isLight ? "rgba(10,10,10,0.12)" : "rgba(255,255,255,0.2)";
  const borderSoft = isLight ? "rgba(10,10,10,0.08)" : "rgba(255,255,255,0.08)";
  const borderFaint = isLight ? "rgba(10,10,10,0.06)" : "rgba(255,255,255,0.06)";
  const surface = isLight ? "rgba(10,10,10,0.04)" : "rgba(255,255,255,0.04)";
  const surfaceActive = isLight ? "rgba(10,10,10,0.08)" : "rgba(255,255,255,0.10)";
  const primaryBg = isLight ? "#0a0a0a" : "#ffffff";
  const primaryFg = isLight ? "#ffffff" : "#0a0a0a";

  return (
    <div
      aria-hidden={!open}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: bg,
        color: fg,
        opacity: open ? 1 : 0,
        pointerEvents: open ? "auto" : "none",
        transition: "opacity 220ms ease, background 180ms ease, color 180ms ease",
        display: "flex",
        flexDirection: "column",
        padding: "18px 22px 28px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          style={{
            width: 40, height: 40, borderRadius: 999,
            border: `1px solid ${borderSoft}`,
            background: "transparent", color: fg,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={20} />
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 32 }}>
        <button
          type="button"
          onClick={handleSignUp}
          style={{
            height: 52, borderRadius: 12, border: "none",
            background: primaryBg, color: primaryFg,
            fontSize: 16, fontWeight: 600, cursor: "pointer",
          }}
        >
          Sign Up
        </button>
        <button
          type="button"
          onClick={handleLogin}
          style={{
            height: 52, borderRadius: 12,
            background: "transparent", color: fg,
            border: `1px solid ${border}`,
            fontSize: 16, fontWeight: 600, cursor: "pointer",
          }}
        >
          Log In
        </button>
      </div>

      <div style={{ height: 1, background: borderSoft, margin: "28px 0" }} />

      <nav style={{ display: "flex", flexDirection: "column" }}>
        {[
          { label: "Home", path: "/" },
          { label: "Pricing", path: "/pricing" },
          { label: "Contact", path: "/#contact" },
        ].map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => goto(item.path)}
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "16px 0", background: "transparent", border: "none",
              color: fg, fontSize: 18, fontWeight: 500, cursor: "pointer",
              borderBottom: `1px solid ${borderFaint}`,
            }}
          >
            <span>{item.label}</span>
            <ArrowRight size={18} style={{ opacity: 0.6 }} />
          </button>
        ))}
      </nav>

      <div style={{ height: 1, background: borderSoft, margin: "28px 0 20px" }} />

      <div style={{ marginTop: "auto" }}>
        <div style={{ fontSize: 12, color: fgFaint, marginBottom: 10, letterSpacing: 0.4 }}>
          Theme
        </div>
        <div
          role="radiogroup"
          style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6,
            background: surface,
            border: `1px solid ${borderSoft}`,
            borderRadius: 12, padding: 4,
          }}
        >
          {themeOptions.map(({ value, label, Icon }) => {
            const active = themePref === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setThemePref(value)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  padding: "10px 8px", borderRadius: 8, cursor: "pointer",
                  background: active ? surfaceActive : "transparent",
                  border: "none",
                  color: active ? fg : fgMuted,
                  fontSize: 12, fontWeight: 500,
                }}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
